import { useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import {
  Check,
  FileSpreadsheet,
  FileUp,
  Loader2,
  Plug,
  Store,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import ModalBase from "components/ModalBase";
import { paths } from "app/paths";
import { verConexaoNuvemshop, verConexaoShopify } from "services/api";
import { downloadCsv } from "utils/downloadCsv";
import {
  confirmarImportacao,
  montarCsvModelo,
  previaImportacao,
  type PedidoDaPrevia,
  type PreviaImportacao,
  type SituacaoPedido,
} from "services/importacao";

type Props = {
  show: boolean;
  title: string;
  setShowNumber: (value: React.SetStateAction<number>) => void;
  logo?: ReactNode;
};

type Etapa = 1 | 2 | 3;

const MAX_MB = 5;

const SITUACAO: Record<SituacaoPedido, { label: string; chip: string; ponto: string }> = {
  PRONTO: { label: "Pronto", chip: "bg-emerald-50 text-emerald-700", ponto: "bg-emerald-500" },
  CORRIGIDO: { label: "Corrigido", chip: "bg-blue-50 text-blue-700", ponto: "bg-brand-primary" },
  AJUSTE: { label: "Precisa de ajuste", chip: "bg-amber-50 text-amber-800", ponto: "bg-amber-500" },
  DUPLICADO: { label: "Duplicado", chip: "bg-slate-100 text-slate-500", ponto: "bg-slate-400" },
};

const moeda = (valor: number, cod: string | null) =>
  `${cod ?? "USD"} ${valor.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const ModalImportarPedidos = ({ show, title, logo, setShowNumber }: Props) => {
  const navigate = useNavigate();

  const [etapa, setEtapa] = useState<Etapa>(1);
  const [lendo, setLendo] = useState(false);
  const [previa, setPrevia] = useState<PreviaImportacao | null>(null);
  const [filtro, setFiltro] = useState<SituacaoPedido | "TODAS">("TODAS");
  const [importando, setImportando] = useState(false);
  const [resultado, setResultado] = useState<{ created: number; skipped: number } | null>(null);
  const [arrastando, setArrastando] = useState(false);

  const [shopify, setShopify] = useState<{ conectada: boolean; loja: string | null } | null>(null);
  const [nuvemshop, setNuvemshop] = useState<boolean | null>(null);

  const inputArquivo = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!show) {
      setEtapa(1);
      setPrevia(null);
      setResultado(null);
      setFiltro("TODAS");
      return;
    }

    // O /shopify/conexao não devolve `connected` — a linha está comentada no
    // backend. Quem diz se está conectada é a presença do shopDomain.
    verConexaoShopify()
      .then(({ data }) => {
        const loja = (data as { loja?: { shopDomain?: string } })?.loja?.shopDomain ?? null;
        setShopify({ conectada: Boolean(loja), loja });
      })
      .catch(() => setShopify({ conectada: false, loja: null }));

    verConexaoNuvemshop()
      .then(({ data }) => setNuvemshop(Boolean(data?.connected)))
      .catch(() => setNuvemshop(false));
  }, [show]);

  const lerPlanilha = async (arquivo: File) => {
    if (arquivo.size > MAX_MB * 1024 * 1024) {
      toast.error(`A planilha passa de ${MAX_MB} MB.`);
      return;
    }

    setLendo(true);
    try {
      const r = await previaImportacao(arquivo);
      setPrevia(r);
      setEtapa(2);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Não foi possível ler a planilha.");
    } finally {
      setLendo(false);
    }
  };

  /** Tudo que não é duplicado entra: o que falta ajuste vira pedido incompleto. */
  const importaveis = useMemo(
    () => (previa?.pedidos ?? []).filter((p) => p.situacao !== "DUPLICADO"),
    [previa],
  );

  const listaFiltrada = useMemo(() => {
    const todos = previa?.pedidos ?? [];
    if (filtro === "TODAS") return todos;
    if (filtro === "PRONTO") {
      return todos.filter((p) => p.situacao === "PRONTO" || p.situacao === "CORRIGIDO");
    }
    return todos.filter((p) => p.situacao === filtro);
  }, [previa, filtro]);

  const importar = async () => {
    if (!previa || !importaveis.length) return;
    setImportando(true);
    try {
      const r = await confirmarImportacao(
        previa.linhas,
        importaveis.map((p) => p.pedido_ref),
      );
      setResultado({ created: r.created, skipped: r.skipped });
      setEtapa(3);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Falha ao importar os pedidos.");
    } finally {
      setImportando(false);
    }
  };

  const contagem = previa?.resumo;
  const comAjuste = previa?.pedidos.filter((p) => p.situacao === "AJUSTE").length ?? 0;
  const valorTotal = importaveis.reduce((acc, p) => acc + (p.total || 0), 0);

  return (
    <ModalBase
      show={show}
      onClose={() => setShowNumber(-1)}
      titulo={title}
      icone={logo ?? <FileUp />}
      largura={etapa === 2 ? "900px" : "620px"}
      corpoSemPadding
      subtitulo={
        etapa === 1
          ? "Traga vários pedidos de uma vez a partir de uma planilha"
          : etapa === 2
            ? `${previa?.arquivo.linhas ?? 0} linhas lidas · confira antes de importar`
            : "Importação concluída"
      }
      acoes={<Passos atual={etapa} />}
      rodape={
        etapa === 2 ? (
          <div className="flex items-center justify-between gap-4 border-t border-slate-100 bg-white px-6 py-3.5">
            <p className="text-[11px] text-slate-400">
              {comAjuste > 0 &&
                `${comAjuste} ${comAjuste === 1 ? "entra incompleto" : "entram incompletos"} e você completa na cotação. `}
              {(contagem?.duplicados ?? 0) > 0 &&
                `${contagem?.duplicados} já ${contagem?.duplicados === 1 ? "existe" : "existem"} e ${contagem?.duplicados === 1 ? "será ignorado" : "serão ignorados"}.`}
            </p>
            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  setPrevia(null);
                  setEtapa(1);
                }}
                className="text-sm text-slate-500 transition-colors hover:text-slate-700"
              >
                Voltar
              </button>
              <button
                onClick={importar}
                disabled={!importaveis.length || importando}
                className="flex h-10 items-center gap-2 rounded-lg bg-brand-sidebar px-4 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-40"
              >
                {importando && <Loader2 className="h-4 w-4 animate-spin" />}
                Importar {importaveis.length}{" "}
                {importaveis.length === 1 ? "pedido" : "pedidos"}
              </button>
            </div>
          </div>
        ) : undefined
      }
    >
      {/* ------------------------------------------------ 1. ARQUIVO */}
      {etapa === 1 && (
        <div className="space-y-4 px-6 py-5">
          <button
            type="button"
            onClick={() => inputArquivo.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setArrastando(true);
            }}
            onDragLeave={() => setArrastando(false)}
            onDrop={(e) => {
              e.preventDefault();
              setArrastando(false);
              const f = e.dataTransfer.files?.[0];
              if (f) lerPlanilha(f);
            }}
            className={`flex w-full flex-col items-center gap-2 rounded-xl border-2 border-dashed px-6 py-10 transition-colors ${
              arrastando
                ? "border-brand-primary bg-blue-50/60"
                : "border-slate-200 bg-slate-50/50 hover:border-slate-300"
            }`}
          >
            {lendo ? (
              <>
                <Loader2 className="h-6 w-6 animate-spin text-brand-primary" />
                <span className="text-sm font-medium text-slate-600">Lendo a planilha…</span>
              </>
            ) : (
              <>
                <span className="grid h-11 w-11 place-items-center rounded-xl bg-white text-brand-primary ring-1 ring-slate-200">
                  <FileSpreadsheet className="h-5 w-5" />
                </span>
                <span className="text-sm font-semibold text-slate-800">
                  Arraste sua planilha aqui
                </span>
                <span className="text-[11px] text-slate-400">
                  ou <span className="font-medium text-brand-primary underline">escolha um arquivo</span> · CSV até {MAX_MB} MB
                </span>
              </>
            )}
          </button>

          <div className="flex items-center gap-3 rounded-xl border border-slate-200 px-3 py-2.5">
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-emerald-50 text-emerald-600">
              <FileSpreadsheet className="h-4 w-4" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-slate-900">
                Primeira vez? Use nosso modelo
              </p>
              <p className="text-[11px] leading-snug text-slate-500">
                Pedido, comprador, endereço, país, itens, SKU, quantidade e valor. O HS Code
                vem do seu cadastro de produtos, pelo SKU — não da planilha.
              </p>
            </div>
            <button
              onClick={() => downloadCsv(montarCsvModelo(), "modelo-pedidos-intrex.csv")}
              className="shrink-0 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50"
            >
              Baixar modelo
            </button>
          </div>

          <p className="text-center text-[11px] text-slate-400">ou importe direto da loja</p>

          <div className="grid grid-cols-2 gap-3">
            <CartaoLoja
              icone={<Store className="h-4 w-4" />}
              cor="#008060"
              nome="Shopify"
              estado={
                shopify === null
                  ? "verificando…"
                  : shopify.conectada
                    ? (shopify.loja ?? "conectada")
                    : "não conectada"
              }
              conectada={shopify?.conectada ?? false}
              onClick={() =>
                navigate(shopify?.conectada ? paths.shopifyProdutos : paths.formShopify)
              }
            />
            <CartaoLoja
              icone={<Plug className="h-4 w-4" />}
              cor="#1F2B45"
              nome="Nuvemshop"
              estado={
                nuvemshop === null
                  ? "verificando…"
                  : nuvemshop
                    ? "conectada"
                    : "conectar loja"
              }
              conectada={Boolean(nuvemshop)}
              onClick={() => navigate(paths.nuvemshopConectar)}
            />
          </div>

          <input
            ref={inputArquivo}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) lerPlanilha(f);
              e.target.value = "";
            }}
          />
        </div>
      )}

      {/* ------------------------------------------------ 2. REVISÃO */}
      {etapa === 2 && previa && (
        <div className="px-6 py-4">
          <div className="mb-3 flex items-center gap-3 rounded-xl border border-slate-200 px-3 py-2">
            <FileSpreadsheet className="h-4 w-4 shrink-0 text-emerald-600" />
            <p className="min-w-0 flex-1 truncate text-sm font-medium text-slate-800">
              {previa.arquivo.nome}
            </p>
            <button
              onClick={() => {
                setPrevia(null);
                setEtapa(1);
              }}
              className="shrink-0 text-xs font-medium text-brand-primary hover:underline"
            >
              Trocar arquivo
            </button>
          </div>

          <div className="mb-3 flex flex-wrap gap-2">
            <Filtro
              label="Todas"
              n={contagem?.total ?? 0}
              ativo={filtro === "TODAS"}
              onClick={() => setFiltro("TODAS")}
            />
            <Filtro
              label="Prontos"
              n={contagem?.prontos ?? 0}
              cor="emerald"
              ativo={filtro === "PRONTO"}
              onClick={() => setFiltro("PRONTO")}
            />
            <Filtro
              label="Precisam de ajuste"
              n={contagem?.ajuste ?? 0}
              cor="amber"
              ativo={filtro === "AJUSTE"}
              onClick={() => setFiltro("AJUSTE")}
            />
            <Filtro
              label="Duplicados"
              n={contagem?.duplicados ?? 0}
              cor="slate"
              ativo={filtro === "DUPLICADO"}
              onClick={() => setFiltro("DUPLICADO")}
            />
          </div>

          <div className="max-h-[320px] overflow-y-auto rounded-xl border border-slate-200">
            <table className="w-full border-collapse text-left">
              <thead className="sticky top-0 bg-slate-50">
                <tr className="text-[10px] uppercase tracking-wider text-slate-500">
                  <th className="px-3 py-2 font-semibold">Pedido</th>
                  <th className="px-3 py-2 font-semibold">Comprador</th>
                  <th className="px-3 py-2 font-semibold">País</th>
                  <th className="px-3 py-2 font-semibold">Itens</th>
                  <th className="px-3 py-2 text-right font-semibold">Valor</th>
                  <th className="px-3 py-2 font-semibold">Situação</th>
                </tr>
              </thead>
              <tbody>
                {listaFiltrada.map((p, i) => (
                  <LinhaPedido key={`${p.pedido_ref}-${i}`} pedido={p} />
                ))}
                {!listaFiltrada.length && (
                  <tr>
                    <td colSpan={6} className="px-3 py-8 text-center text-xs text-slate-400">
                      Nenhum pedido neste filtro.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ------------------------------------------------ 3. PRONTO */}
      {etapa === 3 && resultado && (
        <div className="flex flex-col items-center px-6 py-8 text-center">
          <div className="mb-4 grid h-14 w-14 place-items-center rounded-full bg-emerald-500 text-white">
            <Check className="h-7 w-7" strokeWidth={3} />
          </div>
          <h3 className="text-lg font-semibold text-slate-900">
            {resultado.created} {resultado.created === 1 ? "pedido importado" : "pedidos importados"}
          </h3>
          <p className="mt-1 max-w-[46ch] text-xs leading-relaxed text-slate-400">
            {comAjuste > 0
              ? `${resultado.created - comAjuste} prontos para cotar. ${comAjuste} ${comAjuste === 1 ? "entrou incompleto" : "entraram incompletos"} e você completa o HS Code na cotação.`
              : "Todos prontos para cotar."}
            {resultado.skipped > 0 && ` ${resultado.skipped} já existiam e foram ignorados.`}
          </p>

          <div className="mt-5 grid w-full max-w-md grid-cols-3 gap-3">
            <Estatistica
              rotulo="Prontos"
              valor={String(Math.max(0, resultado.created - comAjuste))}
              cor="text-emerald-600"
            />
            <Estatistica rotulo="Incompletos" valor={String(comAjuste)} cor="text-amber-600" />
            <Estatistica
              rotulo="Valor total"
              valor={moeda(valorTotal, previa?.pedidos[0]?.moeda ?? null)}
            />
          </div>

          <div className="mt-5 flex items-center gap-2">
            <button
              onClick={() => setShowNumber(-1)}
              className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-50"
            >
              Fechar
            </button>
            {/* Abre o fluxo de cotação, que lista os pedidos importados. */}
            <button
              onClick={() => setShowNumber(6)}
              className="rounded-lg bg-brand-sidebar px-3 py-2 text-xs font-semibold text-white transition-opacity hover:opacity-90"
            >
              Cotar agora
            </button>
          </div>
        </div>
      )}
    </ModalBase>
  );
};

export default ModalImportarPedidos;

/* ------------------------------------------------------------------ */

const Passos = ({ atual }: { atual: Etapa }) => (
  <div className="flex items-center gap-1.5">
    {(["Arquivo", "Revisão", "Pronto"] as const).map((nome, i) => {
      const n = (i + 1) as Etapa;
      const ativo = atual === n;
      const feito = atual > n;
      return (
        <span
          key={nome}
          className={`flex items-center gap-1 rounded-full px-2 py-1 text-[10px] font-medium ${
            ativo
              ? "bg-brand-sidebar text-white"
              : feito
                ? "bg-emerald-50 text-emerald-700"
                : "bg-slate-100 text-slate-400"
          }`}
        >
          <span className="font-bold">{n}</span>
          {nome}
        </span>
      );
    })}
  </div>
);

const Filtro = ({
  label,
  n,
  cor = "slate",
  ativo,
  onClick,
}: {
  label: string;
  n: number;
  cor?: "emerald" | "amber" | "slate";
  ativo: boolean;
  onClick: () => void;
}) => {
  const ponto = {
    emerald: "bg-emerald-500",
    amber: "bg-amber-500",
    slate: "bg-slate-400",
  }[cor];

  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs transition-colors ${
        ativo
          ? "bg-brand-sidebar text-white"
          : "border border-slate-200 text-slate-600 hover:bg-slate-50"
      }`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${ativo ? "bg-white/70" : ponto}`} />
      {label} · {n}
    </button>
  );
};

const LinhaPedido = ({ pedido }: { pedido: PedidoDaPrevia }) => {
  const cfg = SITUACAO[pedido.situacao];
  const apagado = pedido.situacao === "DUPLICADO";

  return (
    <tr className={`border-t border-slate-100 ${apagado ? "opacity-50" : ""}`}>
      <td className="px-3 py-2 text-sm font-semibold text-slate-900">{pedido.pedido_ref}</td>
      <td className="px-3 py-2 text-sm text-slate-700">{pedido.comprador ?? "—"}</td>
      <td className="px-3 py-2">
        <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600">
          {pedido.pais ?? "?"}
        </span>
      </td>
      <td className="px-3 py-2 text-sm text-slate-600">
        {pedido.itens} {pedido.itens === 1 ? "item" : "itens"}
      </td>
      <td className="whitespace-nowrap px-3 py-2 text-right text-sm text-slate-700">
        {moeda(pedido.total, pedido.moeda)}
      </td>
      <td className="px-3 py-2">
        <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium ${cfg.chip}`}>
          <span className={`h-1.5 w-1.5 rounded-full ${cfg.ponto}`} />
          {cfg.label}
        </span>
        {pedido.motivo && (
          <span className="ml-2 text-[11px] text-slate-400">{pedido.motivo}</span>
        )}
      </td>
    </tr>
  );
};

const CartaoLoja = ({
  icone,
  cor,
  nome,
  estado,
  conectada,
  onClick,
}: {
  icone: ReactNode;
  cor: string;
  nome: string;
  estado: string;
  conectada: boolean;
  onClick: () => void;
}) => (
  <button
    onClick={onClick}
    className="flex items-center gap-3 rounded-xl border border-slate-200 px-3 py-2.5 text-left transition-colors hover:border-slate-300"
  >
    <span
      className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-white"
      style={{ backgroundColor: cor }}
    >
      {icone}
    </span>
    <span className="min-w-0 flex-1">
      <span className="block text-sm font-semibold text-slate-900">{nome}</span>
      <span
        className={`block truncate text-[11px] ${
          conectada ? "text-emerald-600" : "text-slate-400"
        }`}
      >
        {estado}
      </span>
    </span>
  </button>
);

const Estatistica = ({
  rotulo,
  valor,
  cor = "text-slate-900",
}: {
  rotulo: string;
  valor: string;
  cor?: string;
}) => (
  <div className="rounded-xl bg-slate-50 px-3 py-2.5">
    <p className="text-[10px] leading-tight text-slate-400">{rotulo}</p>
    <p className={`mt-0.5 text-base font-bold ${cor}`}>{valor}</p>
  </div>
);
